import { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { name: '', contact: '', address: '', postal_code: '', city: '', email: '', phone: '', kvk: '', btw_id: '', notes: '' };
const FIELDS = [['name', 'Bedrijfsnaam / naam *'], ['contact', 'Contactpersoon'], ['address', 'Adres'], ['postal_code', 'Postcode'], ['city', 'Plaats'], ['email', 'E-mail'], ['phone', 'Telefoon'], ['kvk', 'KvK'], ['btw_id', 'Btw-id'], ['notes', 'Notities']];

export default function EsquarcioClients({ readOnly }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => axiosInstance.get('/esquarcio/clients').then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await axiosInstance.put(`/esquarcio/clients/${form.id}`, form); else await axiosInstance.post('/esquarcio/clients', form);
      toast.success('Klant opgeslagen'); setForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Opslaan mislukt'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Klant "${c.name}" verwijderen?`)) return;
    try { await axiosInstance.delete(`/esquarcio/clients/${c.id}`); toast.success('Klant verwijderd'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Verwijderen mislukt'); }
  };

  return (
    <div className="space-y-4" data-testid="esq-clients">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{clients.length} klanten — los van de klanten van Cardoso Bouw.</p>
        {!readOnly && <Button onClick={() => setForm({ ...EMPTY })} className="bg-[#0f3b57] hover:bg-[#0b2d43]" data-testid="esq-new-client-btn"><Plus className="w-4 h-4 mr-1" /> Nieuwe klant</Button>}
      </div>
      {form && (
        <Card className="border-[#0f3b57]/30 shadow-sm"><CardContent className="p-4">
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="esq-client-form">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === 'notes' ? 'md:col-span-2' : ''}><Label className="text-xs">{l}</Label><Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k === 'name'} data-testid={`esq-client-${k}`} /></div>
            ))}
            <div className="md:col-span-2 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setForm(null)}><X className="w-4 h-4 mr-1" /> Annuleren</Button>
              <Button type="submit" className="bg-[#0f3b57] hover:bg-[#0b2d43]" data-testid="esq-client-save"><Save className="w-4 h-4 mr-1" /> Opslaan</Button>
            </div>
          </form>
        </CardContent></Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {clients.map((c) => (
          <Card key={c.id} className="border-gray-200 shadow-sm" data-testid={`esq-client-card-${c.id}`}><CardContent className="p-4">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-gray-800 truncate">{c.name}</p>
                {c.contact && <p className="text-xs text-gray-500">{c.contact}</p>}
                <p className="text-xs text-gray-600 mt-1">{[c.address, [c.postal_code, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                <p className="text-xs text-gray-500">{[c.email, c.phone].filter(Boolean).join(' · ')}</p>
                {(c.kvk || c.btw_id) && <p className="text-[11px] text-gray-400 mt-1">{[c.kvk && `KvK ${c.kvk}`, c.btw_id && `Btw ${c.btw_id}`].filter(Boolean).join(' · ')}</p>}
              </div>
              {!readOnly && <div className="flex shrink-0">
                <Button size="sm" variant="ghost" onClick={() => setForm({ ...EMPTY, ...c })} className="h-7 text-gray-600" data-testid={`esq-client-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(c)} className="h-7 text-red-600" data-testid={`esq-client-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>}
            </div>
          </CardContent></Card>
        ))}
        {clients.length === 0 && !form && <p className="text-sm text-gray-400 col-span-full py-8 text-center">Nog geen klanten.</p>}
      </div>
    </div>
  );
}
